import { useQuery } from '@tanstack/react-query';
import {
  BookIcon,
  CalendarDaysIcon,
  ChartLineIcon,
  GiftIcon,
  HeartIcon,
  ImageIcon,
  InfoIcon,
  PawPrintIcon,
  StarIcon,
  UserIcon,
} from 'lucide-preact';
import { ComponentProps, ReactNode } from 'preact/compat';

import { useIsMobileLayout } from '@/components/LayoutDetector/hooks';
import { getHomeURL } from '@/helpers';
import { cn } from '@/lib/utils';
import { getZooQueryOptions } from '@/queries/zoos';
import { useSelector } from '@/redux/helper';
import {
  selectCanViewPartnerAnalytics,
  selectIsAdmin,
  selectIsProfileCompleted,
  selectShowsEducatorsApp,
} from '@/redux/selectors/users';

interface MenuRowProps extends Omit<ComponentProps<'a'>, 'icon'> {
  title: string;
  icon?: ReactNode;
}

interface NavMenuContentProps extends ComponentProps<'div'> {
  onItemClick?: (e: MouseEvent) => void;
}

const MenuRow = ({ title, children, icon, ...rest }: MenuRowProps) => (
  <a
    className="preflight preflight-a flex items-center gap-2 px-6 py-3 text-base font-bold text-stone-900 hover:bg-[#ebebeb]"
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...rest}
  >
    {icon}
    {title && <span>{title}</span>}
    {children}
  </a>
);

const NavMenuContent = ({ className, onItemClick, ...props }: NavMenuContentProps) => {
  const showsEducatorsApp = useSelector(selectShowsEducatorsApp);
  const educatorsAppURL = useSelector((state) => state.remoteConfig.educatorsAppURL);
  const giftCardConfig = useSelector((state) => state.remoteConfig.giftCardConfig);
  const username = useSelector((store) => store.user.username);
  const isProfileCompleted = useSelector(selectIsProfileCompleted);
  const isLoggedIn = !!username;
  const rootDomainURL = getHomeURL();
  const isMobile = useIsMobileLayout();
  const zooId = useSelector((state) => state.user.zooId);
  const canViewPartnerAnalytics = useSelector(selectCanViewPartnerAnalytics);
  // `admin:menu` names no role's rule, so only admin ever passed it.
  const isAdmin = useSelector(selectIsAdmin);
  const { data: zoo } = useQuery({
    ...getZooQueryOptions(zooId),
    enabled: !!zooId && canViewPartnerAnalytics,
  });

  const educatorsAppLink = educatorsAppURL ? new URL(educatorsAppURL) : undefined;
  if (educatorsAppLink && isMobile) {
    educatorsAppLink.pathname = '/about';
  }

  const divider = <div className="mx-6 my-3 h-px bg-stone-300" />;

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div className={cn('py-3', className)} {...props}>
      {!isLoggedIn ? (
        <>
          <MenuRow
            title="About"
            data-item="about"
            icon={<PawPrintIcon className="size-5" />}
            href={`${rootDomainURL}/about-us`}
            onClick={onItemClick}
          />
          {divider}
          <MenuRow
            title="Features"
            data-item="features"
            icon={<StarIcon className="size-5" />}
            href={`${rootDomainURL}/features`}
            onClick={onItemClick}
          />
          {divider}
          <MenuRow
            title="For Educators"
            data-item="educators"
            icon={<BookIcon className="size-5" />}
            href={`${rootDomainURL}/educators`}
            onClick={onItemClick}
          />
          {divider}
          <MenuRow
            title="Events"
            data-item="events"
            icon={<CalendarDaysIcon className="size-5" />}
            href="/talk-schedule"
            onClick={onItemClick}
          />
        </>
      ) : (
        <>
          {zoo?.mpLink && (
            <MenuRow
              title="Partner Analytics"
              data-item="partner-analytics"
              onClick={(e) => {
                window.open(zoo.mpLink);
                onItemClick?.(e);
              }}
              icon={<ChartLineIcon className="size-5" />}
            />
          )}

          {showsEducatorsApp && (
            <MenuRow
              title="Resources"
              data-item="educator-resources"
              href={educatorsAppLink?.toString()}
              onClick={onItemClick}
              icon={<BookIcon className="size-5" />}
            />
          )}

          <MenuRow
            title="My Animals"
            data-item="my-animals"
            href="/habitats"
            onClick={onItemClick}
            icon={<HeartIcon className="size-5" />}
          />
          <MenuRow
            title="My Journal"
            href={`/u/${username}`}
            onClick={onItemClick}
            icon={<ImageIcon className="size-5" />}
          />

          {divider}

          <MenuRow
            title="My Account"
            data-item="my-account"
            href="/account/info"
            onClick={onItemClick}
            icon={<UserIcon className="size-5" />}
          >
            {!isProfileCompleted && (
              <span className="block rounded-full bg-rose-50 px-2 text-[10px]/[1rem] font-medium uppercase text-rose-500">
                Complete profile
              </span>
            )}
          </MenuRow>

          {divider}

          <MenuRow
            title="Events"
            data-item="events"
            icon={<CalendarDaysIcon className="size-5" />}
            href="/schedule"
            onClick={onItemClick}
          />

          {divider}

          <MenuRow
            title="Gift Cards"
            data-item="gift-cards"
            href="/gift"
            onClick={onItemClick}
            icon={
              giftCardConfig?.enabled && giftCardConfig?.iconURL ? (
                <img className="-mx-0.5 -mt-0.5 w-6" src={giftCardConfig.iconURL} alt="gift box" />
              ) : (
                <GiftIcon className="size-5" />
              )
            }
          >
            {giftCardConfig?.enabled && giftCardConfig?.badge && (
              <span className="block rounded-full bg-olive-4 px-2 text-[10px]/[1rem] font-medium">
                {giftCardConfig.badge}
              </span>
            )}
          </MenuRow>

          <MenuRow
            title="Support & FAQs"
            data-item="help"
            href="/help"
            onClick={onItemClick}
            icon={<InfoIcon className="size-5" />}
          />

          {isAdmin && (
            <>
              {divider}
              <MenuRow title="Admin - Users" data-item="admin-users" href="/admin/users" onClick={onItemClick} />
              <MenuRow title="Admin - Habitats" data-item="admin-habitats" href="/admin/habitats" onClick={onItemClick} />
              <MenuRow title="Admin - Partners" data-item="admin-partners" href="/admin/partners" onClick={onItemClick} />
              <MenuRow
                title="Admin - Camera Settings"
                data-item="admin-camera-settings"
                href="/admin/camera/settings"
                onClick={onItemClick}
              />
              <MenuRow
                title="Admin - Camera Info"
                data-item="admin-camera-info"
                href="/admin/camera/info"
                onClick={onItemClick}
              />
              <MenuRow title="Admin - Playlists" href="/admin/playlists" onClick={onItemClick} />
              <MenuRow title="Admin - Announcements" href="/admin/announcements" onClick={onItemClick} />
              <MenuRow title="Admin - Advertisements" href="/admin/advertisements" onClick={onItemClick} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default NavMenuContent;
